// Presentation / demo mode (backend presentation.py; showcase follow-up
// 2026-10-08). A per-session DISPLAY mask for filming and marketing: while it
// is on, real supplier names render as "Supplier A/B/...", tender /
// rate-set / tender-document file names as "Tender file A.pdf" (showcase
// take 3: file names named real contracts and suppliers), money in the
// procurement views (and any amount on the same row as a supplier name, e.g.
// a project's tenders and invoices) renders rounded ("≈$410"), and with
// "mask staff" other people's names render as "Colleague A/B/...". The
// viewer's own name is never masked.
//
// Purely a mask: nothing is sent back and no data changes. It works on the
// rendered DOM (a MutationObserver re-applies it to whatever renders next),
// so the views themselves need no changes and can't forget to mask.
//
// Hooks: the header "Present" toggle (managers / platform admins), the URL
// (?presentation=1, &mask_staff=1; ?presentation=0 turns it off), or the API
// (POST /api/auth/presentation {on, mask_staff}) - the showcase scripts use
// the API (scripts/showcase/showcase_lib.py Recorder.actor(presentation=True)).
import { getPresentationDictionary, setPresentationMode, type PresentationDictionary } from "./api";
import { getState, type PresentationState } from "./state";
import "./styles/presentation.css";
import { showToast } from "./toast";

// Money is rounded inside these views (procurement workspace panes).
const MONEY_SCOPE = '[id^="proc-"][id$="-container"], #tender-map-container';
// A row that names a supplier: its amounts are tendered / invoiced values.
const ROW_SCOPE = "tr, li, .pm-task-row, .pr-strip-row";
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "NOSCRIPT"]);
const MONEY_RE = /(^|[^≈\w$])\$\s?(\d[\d,]*)(\.\d+)?/g;

let active = false;
let maskStaff = false;
let supplierRe: RegExp | null = null;
let staffRe: RegExp | null = null;
let fileRe: RegExp | null = null;
const supplierLabel = new Map<string, string>();
const staffLabel = new Map<string, string>();
const fileLabel = new Map<string, string>();
let observer: MutationObserver | null = null;

export function presentationOn(): boolean {
  return active;
}

function esc(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildRe(groups: { label: string; names: string[] }[], into: Map<string, string>): RegExp | null {
  into.clear();
  for (const g of groups) {
    for (const n of g.names) {
      const k = n.trim();
      if (k.length >= 3) into.set(k.toLowerCase(), g.label);
    }
  }
  if (!into.size) return null;
  const alts = [...into.keys()].sort((a, b) => b.length - a.length).map(esc);
  return new RegExp(`(^|[^\\w])(${alts.join("|")})(?![\\w])`, "gi");
}

/** "Supplier A" for a real supplier name (the name itself when off or unknown). */
export function maskSupplier(name: string | null | undefined): string {
  if (!name) return name ?? "";
  if (!active) return name;
  return supplierLabel.get(name.trim().toLowerCase()) ?? "Supplier";
}

/** Another person's name -> "Colleague A" when staff masking is on. */
export function maskPerson(name: string | null | undefined, isSelf = false): string {
  if (!name || !active || !maskStaff || isSelf) return name ?? "";
  return staffLabel.get(name.trim().toLowerCase()) ?? "Colleague";
}

/** Two significant figures (a tendered rate or amount, rounded for display). */
export function maskRate(v: number): number {
  if (!active || !Number.isFinite(v) || v === 0) return v;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.abs(v))) - 1);
  return Math.round(v / mag) * mag;
}

function roundMoneyText(s: string): string {
  return s.replace(MONEY_RE, (_m, pre: string, int: string, dec: string | undefined) => {
    const v = Number(int.replace(/,/g, "") + (dec ?? ""));
    if (!Number.isFinite(v)) return _m;
    const r = maskRate(v);
    return `${pre}≈$${r >= 100 ? Math.round(r).toLocaleString() : String(Number(r.toPrecision(2)))}`;
  });
}

function replaceNames(s: string): { out: string; supplier: boolean } {
  let out = s;
  let supplier = false;
  // file names first: one may contain a supplier name ("... Civil Ltd - NZS3910.pdf"), and the
  // whole name is what gives the contract away
  if (fileRe) {
    out = out.replace(fileRe, (_m, pre: string, name: string) => {
      supplier = true; // a tender document's row: round its amounts too
      return pre + (fileLabel.get(name.toLowerCase()) ?? "Tender file");
    });
  }
  if (supplierRe) {
    out = out.replace(supplierRe, (_m, pre: string, name: string) => {
      supplier = true;
      return pre + (supplierLabel.get(name.toLowerCase()) ?? "Supplier");
    });
  }
  if (maskStaff && staffRe) {
    out = out.replace(staffRe, (_m, pre: string, name: string) => pre + (staffLabel.get(name.toLowerCase()) ?? "Colleague"));
  }
  return { out, supplier };
}

function skipped(node: Node): boolean {
  const elx = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  if (!elx) return true;
  if (SKIP_TAGS.has(elx.tagName)) return true;
  return !!elx.closest("[data-pres-skip]");
}

function maskTextNode(t: Text, rows: Set<Element>): void {
  if (skipped(t)) return;
  const before = t.data;
  if (!before.trim()) return;
  const { out, supplier } = replaceNames(before);
  let next = out;
  const parent = t.parentElement;
  if (supplier && parent) {
    const row = parent.closest(ROW_SCOPE);
    if (row) rows.add(row);
  }
  if (parent?.closest(MONEY_SCOPE)) next = roundMoneyText(next);
  if (next !== before) t.data = next;
}

function maskAttrs(e: Element): void {
  for (const attr of ["title", "aria-label", "placeholder"]) {
    const v = e.getAttribute(attr);
    if (!v) continue;
    let { out } = replaceNames(v);
    if (e.closest(MONEY_SCOPE)) out = roundMoneyText(out);
    if (out !== v) e.setAttribute(attr, out);
  }
}

function maskTree(root: Node): void {
  if (!active || skipped(root)) return;
  const rows = new Set<Element>();
  if (root.nodeType === Node.TEXT_NODE) {
    maskTextNode(root as Text, rows);
  } else if (root.nodeType === Node.ELEMENT_NODE) {
    const e = root as Element;
    maskAttrs(e);
    for (const x of e.querySelectorAll("[title], [aria-label], [placeholder]")) maskAttrs(x);
    const walker = document.createTreeWalker(e, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    while (walker.nextNode()) texts.push(walker.currentNode as Text);
    for (const t of texts) maskTextNode(t, rows);
  }
  // amounts on a row that names a supplier (project tenders, invoices, ...)
  for (const row of rows) {
    const walker = document.createTreeWalker(row, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const t = walker.currentNode as Text;
      if (skipped(t)) continue;
      const next = roundMoneyText(t.data);
      if (next !== t.data) t.data = next;
    }
  }
}

function startObserver(): void {
  observer?.disconnect();
  observer = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === "characterData") maskTree(r.target);
      else if (r.type === "attributes") maskAttrs(r.target as Element);
      else r.addedNodes.forEach((n) => maskTree(n));
    }
  });
  observer.observe(document.body, {
    childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["title"],
  });
}

async function applyDictionary(): Promise<void> {
  let dict: PresentationDictionary;
  try {
    dict = await getPresentationDictionary();
  } catch {
    dict = { suppliers: [], staff: [] };
  }
  supplierRe = buildRe(dict.suppliers, supplierLabel);
  staffRe = buildRe(dict.staff, staffLabel);
  fileRe = buildRe(dict.files ?? [], fileLabel);
}

function renderBadge(state: PresentationState): void {
  document.getElementById("presentation-badge")?.remove();
  document.getElementById("presentation-toggle")?.remove();
  if (!state.allowed) return;
  const anchor = document.getElementById("current-user-info");
  if (!anchor) return;
  const wrap = document.createElement("span");
  wrap.id = "presentation-toggle";
  wrap.className = "pres-toggle";
  wrap.dataset.presSkip = "1";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = state.on ? "pres-btn pres-on" : "pres-btn";
  btn.textContent = state.on ? "Presentation mode" : "Present";
  btn.title = state.on
    ? "Presentation mode is on: supplier names, tender file names and tendered rates are masked on screen (data unchanged). Click to change."
    : "Presentation mode: mask supplier names, tender file names and tendered rates on screen (this session only; data unchanged)";
  const menu = document.createElement("div");
  menu.className = "pres-menu";
  menu.hidden = true;
  const mk = (label: string, checked: boolean): HTMLInputElement => {
    const l = document.createElement("label");
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = checked;
    l.append(cb, document.createTextNode(` ${label}`));
    menu.append(l);
    return cb;
  };
  const onCb = mk("Mask supplier names, tender file names and tendered rates", state.on);
  const staffCb = mk("Also mask other staff names", state.mask_staff);
  const note = document.createElement("p");
  note.className = "caption";
  note.textContent = "Display only, for this session. Nothing is changed or saved.";
  const apply = document.createElement("button");
  apply.type = "button";
  apply.textContent = "Apply";
  apply.addEventListener("click", () => {
    apply.disabled = true;
    setPresentationMode(onCb.checked, onCb.checked && staffCb.checked)
      .then(() => window.location.reload())
      .catch(() => {
        apply.disabled = false;
        showToast("Could not change presentation mode");
      });
  });
  menu.append(note, apply);
  btn.addEventListener("click", () => { menu.hidden = !menu.hidden; });
  wrap.append(btn, menu);
  anchor.after(wrap);
}

/** ?presentation=1 (&mask_staff=1) / ?presentation=0: switch, strip the
 * query and return the new state (null when the URL says nothing). */
async function fromUrl(state: PresentationState): Promise<PresentationState | null> {
  const url = new URL(window.location.href);
  const q = url.searchParams.get("presentation");
  if (q === null) return null;
  const want = q === "1" || q === "on" || q === "true";
  const staff = want && ["1", "on", "true"].includes(url.searchParams.get("mask_staff") ?? "");
  url.searchParams.delete("presentation");
  url.searchParams.delete("mask_staff");
  window.history.replaceState(null, "", url.toString());
  if (!state.allowed) return null;
  try {
    const s = await setPresentationMode(want, staff);
    return { ...s, allowed: state.allowed };
  } catch {
    return null;
  }
}

/** Called once after login (main.ts runApp). */
export async function initPresentationMode(): Promise<void> {
  const user = getState().currentUser;
  let state: PresentationState = user?.presentation ?? { on: false, mask_staff: false, allowed: false };
  state = (await fromUrl(state)) ?? state;
  renderBadge(state);
  if (!state.on) return;
  await applyDictionary();
  active = true;
  maskStaff = state.mask_staff;
  document.body.classList.add("presentation-mode");
  maskTree(document.body);
  startObserver();
}
